import { useEffect, useState, type ReactNode } from 'react'
import slide1 from '@/assets/slide-1.webp'
import slide2 from '@/assets/slide-2.webp'
import slide3 from '@/assets/slide-3.webp'
import slide4 from '@/assets/slide-4.webp'
import styles from './Auth.module.css'

/**
 * Слайды витрины. Тексты — из макета, дословно.
 *
 * Картинки из Figma ужаты до 828px по ширине (в макете слайд 414px,
 * то есть @2x) и переведены в WebP: исходные PNG весят по мегабайту
 * каждый и на экране всё равно сжимаются вчетверо. Четыре мегабайта
 * ради украшения — это секунда пустого экрана на входе.
 */
const SLIDES = [
  { title: 'Приём платежей в криптовалюте', note: 'Поддержка популярных криптовалют для оплаты.', art: slide1 },
  { title: 'Управление активами', note: 'Балансы, поступления, выводы и история операций.', art: slide2 },
  { title: 'Расширение возможностей', note: 'Инструменты для работы с платежами и оборотами.', art: slide3 },
  { title: 'Комиссия не более 5%', note: 'Выгодные условия для бизнеса и партнёров.', art: slide4 },
] as const

const ROTATE_MS = 6000

/**
 * Оболочка экранов входа: форма слева, витрина справа.
 *
 * Витрина скрывается ниже 1000px — она украшение, а форма нет.
 * Карусель останавливается, как только человек ткнул в точку: дальше
 * он смотрит то, что выбрал, а не то, что подсунул таймер.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const [slide, setSlide] = useState(0)
  const [autoplay, setAutoplay] = useState(true)

  useEffect(() => {
    if (!autoplay) return
    const id = window.setInterval(
      () => setSlide((n) => (n + 1) % SLIDES.length),
      ROTATE_MS,
    )
    return () => window.clearInterval(id)
  }, [autoplay])

  const current = SLIDES[slide] ?? SLIDES[0]

  return (
    <div className={styles.split}>
      <div className={styles.left}>
        {children}
        <span className={styles.foot}>{`© CryptoChangeK, ${new Date().getFullYear()}`}</span>
      </div>

      {/*
        Без `aria-hidden` на всей витрине. Внутри — настоящие кнопки
        переключения слайдов: спрятанные от читалки, они всё равно
        остаются в порядке табуляции, и человек с клавиатуры попадает
        фокусом в элемент, о котором ему ничего не сообщат.
        Декоративны здесь только картинки — у них пустой `alt`.
      */}
      <aside className={styles.right} aria-label="О сервисе">
        <span className={styles.brand}>CryptoChangeK</span>
        {/*
          Все четыре слайда в DOM сразу и переключаются прозрачностью.
          Подменять `src` нельзя: браузер тянет картинку в момент
          переключения, и на медленной сети витрина моргает пустотой
          посреди плавного перехода. Вместе они весят 139 КБ.
        */}
        <div className={styles.art}>
          {SLIDES.map((item, index) => (
            <img
              key={item.title}
              src={item.art}
              alt=""
              className={index === slide ? `${styles.artImg} ${styles.artOn}` : styles.artImg}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>
        <div className={styles.slideText}>
          <span className={styles.slideTitle}>{current.title}</span>
          <span className={styles.slideNote}>{current.note}</span>
        </div>
        <div className={styles.dots} role="tablist" aria-label="Слайды">
          {SLIDES.map((item, index) => (
            <button
              key={item.title}
              type="button"
              className={index === slide ? `${styles.dot} ${styles.on}` : styles.dot}
              onClick={() => { setSlide(index); setAutoplay(false) }}
              role="tab"
              aria-selected={index === slide}
              aria-label={item.title}
            />
          ))}
        </div>
      </aside>
    </div>
  )
}

interface CardProps {
  title: string
  sub?: string
  children: ReactNode
}

/** Карточка формы: заголовок, подпись, содержимое. */
export function AuthCard({ title, sub, children }: CardProps) {
  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <h1 className={styles.title}>{title}</h1>
        {sub ? <span className={styles.sub}>{sub}</span> : null}
      </div>
      {children}
    </div>
  )
}

export { styles as authStyles }
